'use client';

import { useRef } from 'react';
import clsx from 'clsx';
import { FacetChoice } from '@akinon/next/types';
import { usePathname } from 'next/navigation';
import { getChoiceDepth, getTopDepth } from './category-filter-settings';

const INDENT_REM_PER_LEVEL = 1;

/**
 * Bir kategori seçildiğinde servis yalnızca o dalın seçeneklerini döndürüyor;
 * bu da kullanıcının kardeş kategorilere dönmesini imkânsız hâle getiriyor.
 * Bu yüzden seçim yapılmadan önce görülen geniş liste hatırlanır ve daralan
 * yanıtlarda o liste kullanılmaya devam edilir.
 *
 * Hafıza yalnızca seçim aktifken tutulur: seçim yoksa (ya da chip ile
 * kaldırıldığında) servis tüm ağacı döndürdüğü için güncel liste tek doğru
 * kaynaktır — aksi hâlde uzunluk karşılaştırması eski listeyi ekranda tutar ve
 * ancak accordion kapanıp açılınca (bileşen unmount olunca) düzelir.
 * Sayfa değişiminde de hafıza sıfırlanır.
 */
const useStickyChoices = (
  choices: FacetChoice[],
  isSelectionActive: boolean
) => {
  const memoryRef = useRef<FacetChoice[]>([]);
  const pathnameRef = useRef<string | null>(null);
  const pathname = usePathname();

  if (pathnameRef.current !== pathname || !isSelectionActive) {
    pathnameRef.current = pathname;
    memoryRef.current = [];
  }

  if (choices.length > memoryRef.current.length) {
    memoryRef.current = choices;
  }

  return memoryRef.current.length ? memoryRef.current : choices;
};

interface Props {
  choices: FacetChoice[];
  selectedValue: string | null;
  isPending: boolean;
  onSelect: (choice: FacetChoice) => void;
}

export const CategoryTreeFilter = ({
  choices,
  selectedValue,
  isPending,
  onSelect
}: Props) => {
  const treeChoices = useStickyChoices(choices, selectedValue !== null);
  const topDepth = getTopDepth(treeChoices);

  return (
    <ul className="flex flex-col">
      {treeChoices.map((choice) => {
        // `real_depth` gelmeyen seçenekler en üst seviye kabul edilir.
        const level = Math.max(getChoiceDepth(choice) - topDepth, 0);
        const isSelected =
          selectedValue !== null &&
          String(choice.value) === String(selectedValue);

        return (
          <li key={`${choice.label}-${choice.value}`}>
            <button
              type="button"
              onClick={() => onSelect(choice)}
              disabled={isPending}
              data-testid={`${choice.label.trim()}`}
              style={{ paddingLeft: `${level * INDENT_REM_PER_LEVEL}rem` }}
              className={clsx(
                'w-full text-left text-sm py-1 transition-colors font-normal',
                isSelected
                  ? 'text-primary-black font-bold'
                  : 'text-black-800 hover:text-primary-black'
              )}
            >
              {choice.label} ({choice.quantity})
            </button>
          </li>
        );
      })}
    </ul>
  );
};
